"use client";

/**
 * Global toast sanitizer.
 *
 * `sonner` exports `toast` as a single shared object instance. Every module
 * that does `import { toast } from "sonner"` references that same object, so
 * wrapping its methods once here transparently scrubs internal infrastructure
 * terms (see {@link hideInternalTerms}) from every toast in the app — no need
 * to touch the ~170 call sites.
 *
 * Only string content is scrubbed (message + `description`); ReactNode / JSX
 * payloads pass through untouched. Imported once for its side effect by
 * {@link ./app-toaster}, which mounts per shell (dashboard + portal).
 */

import { hideInternalTerms } from "@v1/backend/logic/invite_email_rate_limit";
import { toast } from "sonner";

type ToastStringMethod = typeof toast.error;
type ToastMessage = Parameters<ToastStringMethod>[0];
type ToastData = NonNullable<Parameters<ToastStringMethod>[1]>;

function sanitizeMessage(message: ToastMessage): ToastMessage {
  return typeof message === "string" ? hideInternalTerms(message) : message;
}

function sanitizeData(data: ToastData | undefined): ToastData | undefined {
  if (!data || typeof data.description !== "string") return data;
  return { ...data, description: hideInternalTerms(data.description) };
}

function wrap(method: ToastStringMethod): ToastStringMethod {
  return (message, data) =>
    method(sanitizeMessage(message), sanitizeData(data));
}

// Runs once: ESM evaluates this module a single time and every importer shares
// the same `toast` object, so these method reassignments are seen app-wide.
toast.error = wrap(toast.error);
toast.success = wrap(toast.success);
toast.info = wrap(toast.info);
toast.warning = wrap(toast.warning);
toast.message = wrap(toast.message);
toast.loading = wrap(toast.loading);
